import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listProjects,
  listTickets,
  listMembers,
  createTicket,
  updateTicket,
  getMe,
  type Ticket,
  type TicketStatus,
  type TicketPriority,
} from "@/lib/api";
import {
  STATUS_COLUMNS,
  STATUS_COLOR,
  PRIORITY_META,
  PRIORITY_ORDER,
  byPriority,
  boardOrder,
} from "@/lib/board";
import { Modal } from "@/components/Modal";
import { Avatar } from "@/components/Avatar";
import { Kanban } from "@/components/Kanban";
import { AssigneeFilter, UNASSIGNED } from "@/components/board/AssigneeFilter";
import { TicketModal } from "@/components/board/TicketModal";

export function BoardPage() {
  const { projectId = "" } = useParams();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [assigneeFilter, setAssigneeFilter] = useState<Set<string>>(new Set());

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: listProjects });
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: listMembers });
  const project = projects?.find((p) => p.id === projectId);
  const ticketsKey = ["tickets", projectId] as const;
  const { data: tickets } = useQuery({
    queryKey: ticketsKey,
    queryFn: () => listTickets(projectId),
  });

  // Jira-style filtering happens client-side: all tickets are loaded anyway.
  const visibleTickets = useMemo(() => {
    if (!tickets || assigneeFilter.size === 0) return tickets ?? [];
    return tickets.filter((t) =>
      t.assigneeId ? assigneeFilter.has(t.assigneeId) : assigneeFilter.has(UNASSIGNED),
    );
  }, [tickets, assigneeFilter]);

  /**
   * Reading order of the board — down each column in turn, exactly as Kanban
   * lays them out. The arrows walk this, so "next" always means the card the
   * eye would go to next, and the current filter is respected rather than
   * silently jumping to a ticket that isn't on screen.
   */
  const orderedTickets = useMemo(() => boardOrder(visibleTickets), [visibleTickets]);
  const openIndex = orderedTickets.findIndex((t) => t.id === openTicketId);

  const move = useMutation({
    mutationFn: (vars: { id: string; status: TicketStatus }) =>
      updateTicket(vars.id, { status: vars.status }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ticketsKey });
      const prev = qc.getQueryData<Ticket[]>(ticketsKey);
      qc.setQueryData<Ticket[]>(ticketsKey, (old) =>
        old?.map((t) => (t.id === vars.id ? { ...t, status: vars.status } : t)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(ticketsKey, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: ["tickets", projectId] }),
  });

  return (
    <div>
      <header className="mb-6">
        <Link to="/board" className="text-sm text-text-muted hover:text-text-heading">
          <i className="fa-solid fa-arrow-left mr-1.5 text-xs" />
          Projects
        </Link>
        <div className="mt-2 flex items-end justify-between">
          <div className="flex items-center gap-3">
            {project && (
              <span className="rounded-token-sm bg-accent-muted px-2 py-1 font-mono text-xs font-semibold text-accent">
                {project.key}
              </span>
            )}
            <h1 className="page-title">{project?.name ?? "Board"}</h1>
          </div>
          <div className="flex items-center gap-4">
            <AssigneeFilter
              members={members ?? []}
              selected={assigneeFilter}
              onChange={setAssigneeFilter}
            />
            <button
              onClick={() => setAddOpen(true)}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ color: "var(--color-on-accent)" }}
            >
              <i className="fa-solid fa-plus mr-1.5 text-xs" />
              Create
            </button>
          </div>
        </div>
      </header>

      <Kanban
        columns={STATUS_COLUMNS.map((c) => ({
          key: c.status,
          label: c.label,
          color: STATUS_COLOR[c.status],
        }))}
        items={visibleTickets}
        idOf={(t) => t.id}
        columnOf={(t) => t.status}
        onMove={(id, status) => move.mutate({ id, status })}
        onCardClick={(t) => setOpenTicketId(t.id)}
        sortItems={byPriority}
        renderCard={(t) => {
          const pr = PRIORITY_META[t.priority];
          return (
            <>
              <p className="text-sm font-medium leading-snug text-text-heading">{t.title}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                  <i
                    className={`fa-solid ${pr.icon} text-[11px]`}
                    style={{ color: pr.color }}
                    title={pr.label}
                  />
                  <span className="font-mono text-[11px] text-text-muted">
                    {project?.key}-{t.number}
                  </span>
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-text-secondary">
                  {t.assignee ? (
                    <>
                      <Avatar
                        name={t.assignee.name}
                        url={t.assignee.avatarUrl}
                        size={18}
                        title={t.assignee.name}
                      />
                      <span className="max-w-[110px] truncate">
                        {t.assignee.name.split(" ")[0]}
                      </span>
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-user text-[9px] text-text-muted" />
                      <span className="max-w-[110px] truncate">Unassigned</span>
                    </>
                  )}
                </span>
              </div>
            </>
          );
        }}
      />

      {addOpen && (
        <NewTicketModal
          projectId={projectId}
          onClose={() => setAddOpen(false)}
          defaultAssignee={me?.id ?? null}
        />
      )}
      {openTicketId && (
        <TicketModal
          ticketId={openTicketId}
          projectKey={project?.key ?? ""}
          onClose={() => setOpenTicketId(null)}
          nav={
            openIndex === -1
              ? undefined
              : {
                  index: openIndex,
                  total: orderedTickets.length,
                  goTo: (i) => setOpenTicketId(orderedTickets[i].id),
                }
          }
        />
      )}
    </div>
  );
}

function NewTicketModal({
  projectId,
  onClose,
  defaultAssignee,
}: {
  projectId: string;
  onClose: () => void;
  defaultAssignee: string | null;
}) {
  const qc = useQueryClient();
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: listMembers });
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState<string>(defaultAssignee ?? "");
  const [status, setStatus] = useState<TicketStatus>("todo");
  const [priority, setPriority] = useState<TicketPriority>("medium");

  const create = useMutation({
    mutationFn: () =>
      createTicket(projectId, {
        title,
        description: description || undefined,
        assigneeId: assigneeId || null,
        status,
        priority,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets", projectId] });
      onClose();
    },
  });

  const inputCls =
    "mb-3 w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title="Create ticket">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="mb-1 block text-xs font-medium text-text-muted">Title</label>
        <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} required />

        <label className="mb-1 block text-xs font-medium text-text-muted">Description</label>
        <textarea
          className={inputCls}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Assignee</label>
            <select className={inputCls} value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
              <option value="">Unassigned</option>
              {members?.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Priority</label>
            <select
              className={inputCls}
              value={priority}
              onChange={(e) => setPriority(e.target.value as TicketPriority)}
            >
              {PRIORITY_ORDER.map((p) => (
                <option key={p} value={p}>{PRIORITY_META[p].label}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-text-muted">Status</label>
            <select className={inputCls} value={status} onChange={(e) => setStatus(e.target.value as TicketStatus)}>
              {STATUS_COLUMNS.map((s) => (
                <option key={s.status} value={s.status}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-1 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-token-md border border-border px-4 py-2 text-sm text-text-secondary hover:text-text-heading"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ color: "var(--color-on-accent)" }}
          >
            {create.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
